import { useState } from 'react';
import { Button } from './ui/button';
import { Mic, Video, Settings, RefreshCw, AlertTriangle, Shield, Loader2 } from 'lucide-react';

function getSettingsInstructions() {
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  const isChrome = /Chrome/.test(ua) && !/Edge/.test(ua);
  const isFirefox = /Firefox/.test(ua);
  const isAndroid = /Android/.test(ua);

  if (isIOS && isSafari) {
    return {
      browser: 'Safari (iOS)',
      steps: [
        'Open the Settings app on your iPhone/iPad',
        'Scroll down and tap Safari',
        'Tap Camera (or Microphone)',
        'Select "Allow" for this website',
        'Return to this page and tap "Try Again"',
      ],
    };
  }
  if (isIOS && isChrome) {
    return {
      browser: 'Chrome (iOS)',
      steps: [
        'Open the Settings app on your iPhone/iPad',
        'Scroll down and tap Chrome',
        'Enable Camera and Microphone access',
        'Return to this page and tap "Try Again"',
      ],
    };
  }
  if (isAndroid && isChrome) {
    return {
      browser: 'Chrome (Android)',
      steps: [
        'Tap the lock icon (or tune icon) in the address bar',
        'Tap "Permissions" or "Site settings"',
        'Set Camera and Microphone to "Allow"',
        'Tap "Try Again" below',
      ],
    };
  }
  if (isChrome) {
    return {
      browser: 'Chrome',
      steps: [
        'Click the lock/tune icon in the address bar (left side)',
        'Find Camera and Microphone in the list',
        'Change both to "Allow"',
        'Refresh the page or tap "Try Again" below',
      ],
    };
  }
  if (isFirefox) {
    return {
      browser: 'Firefox',
      steps: [
        'Click the lock icon in the address bar',
        'Click "Connection secure" > "More information"',
        'Go to Permissions tab',
        'Allow Camera and Microphone',
        'Tap "Try Again" below',
      ],
    };
  }
  if (isSafari) {
    return {
      browser: 'Safari (Mac)',
      steps: [
        'Go to Safari > Settings > Websites',
        'Click Camera (and Microphone) in the sidebar',
        'Find this website and set it to "Allow"',
        'Tap "Try Again" below',
      ],
    };
  }
  return {
    browser: 'your browser',
    steps: [
      'Open your browser settings or site permissions',
      'Find Camera and Microphone settings',
      'Allow access for this website',
      'Return here and tap "Try Again"',
    ],
  };
}

export default function PermissionGate({ permissionStatus, callType, onRetry, onCancel }) {
  const [showGuide, setShowGuide] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const isBlocked = permissionStatus === 'blocked';
  const isRequesting = permissionStatus === 'requesting' || requesting;
  const isVideo = callType === 'video';
  const instructions = getSettingsInstructions();

  // CRITICAL: Call getUserMedia DIRECTLY in the click handler for Safari compatibility
  // Safari requires getUserMedia to be in the synchronous call stack of a user gesture
  const handleEnableAccess = async () => {
    console.log('[PermissionGate] Enable Access clicked — calling getUserMedia directly');
    setRequesting(true);

    try {
      // Call getUserMedia IMMEDIATELY — no state updates before this
      const constraints = {
        audio: true,
        video: isVideo ? { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } } : false,
      };
      console.log('[PermissionGate] getUserMedia constraints:', JSON.stringify(constraints));

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('[PermissionGate] getUserMedia SUCCESS — stream acquired, tracks:', stream.getTracks().length);

      // Permission granted — pass stream to parent via onRetry
      // The parent (retryPermission) will handle the stream and resume the call
      setRequesting(false);
      onRetry(stream);
    } catch (err) {
      console.log('[PermissionGate] getUserMedia FAILED:', err.name, err.message);
      setRequesting(false);
      // Let the parent handle the error state update
      onRetry(null, err);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4" data-testid="permission-gate">
      <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-sm shadow-2xl animate-in fade-in zoom-in-95 duration-300">
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isBlocked ? 'bg-red-500/15' : isRequesting ? 'bg-cyan-500/15' : 'bg-amber-500/15'}`}>
            {isRequesting ? (
              <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
            ) : isBlocked ? (
              <Shield className="w-8 h-8 text-red-400" />
            ) : (
              <AlertTriangle className="w-8 h-8 text-amber-400" />
            )}
          </div>
        </div>

        {/* Title */}
        <h2 className="text-lg font-bold text-white text-center mb-1" data-testid="permission-title">
          {isRequesting ? 'Requesting Access...' : isBlocked ? 'Permission Blocked' : 'Permission Required'}
        </h2>

        {/* Description */}
        <p className="text-sm text-muted-foreground text-center mb-5" data-testid="permission-description">
          {isRequesting
            ? 'Please allow access when your browser prompts you.'
            : isBlocked
            ? `${isVideo ? 'Camera and microphone' : 'Microphone'} access is blocked by your browser. Update your settings to make calls.`
            : `To ${isVideo ? 'make a video call' : 'make a voice call'}, please allow ${isVideo ? 'camera and microphone' : 'microphone'} access when prompted.`
          }
        </p>

        {/* Required permissions icons */}
        <div className="flex items-center justify-center gap-4 mb-5">
          <div className="flex items-center gap-1.5 text-xs text-foreground/80 bg-muted/30 px-3 py-1.5 rounded-full">
            <Mic className="w-3.5 h-3.5 text-cyan-400" /> Microphone
          </div>
          {isVideo && (
            <div className="flex items-center gap-1.5 text-xs text-foreground/80 bg-muted/30 px-3 py-1.5 rounded-full">
              <Video className="w-3.5 h-3.5 text-cyan-400" /> Camera
            </div>
          )}
        </div>

        {/* Settings Guide (for blocked) */}
        {isBlocked && (
          <>
            <Button data-testid="open-settings-guide-btn" onClick={() => setShowGuide(!showGuide)}
              variant="outline" className="w-full mb-3 border-border text-foreground/80 hover:text-foreground hover:bg-muted/30">
              <Settings className="w-4 h-4 mr-2" />
              {showGuide ? 'Hide Settings Guide' : 'Open Settings Guide'}
            </Button>

            {showGuide && (
              <div className="bg-muted/30 border border-border rounded-xl p-4 mb-4 animate-in fade-in slide-in-from-top-2 duration-200" data-testid="settings-guide">
                <p className="text-xs font-semibold text-foreground/80 mb-2">
                  For {instructions.browser}:
                </p>
                <ol className="space-y-1.5">
                  {instructions.steps.map((step, i) => (
                    <li key={i} className="text-xs text-muted-foreground flex gap-2">
                      <span className="shrink-0 w-4 h-4 rounded-full bg-violet-500/20 text-violet-400 text-[10px] flex items-center justify-center font-bold">{i + 1}</span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </>
        )}

        {/* Action Buttons */}
        <div className="space-y-2">
          <Button
            data-testid="enable-access-btn"
            onClick={handleEnableAccess}
            disabled={isRequesting}
            className="w-full bg-violet-600 hover:bg-violet-700 text-white font-medium"
          >
            {isRequesting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            {isRequesting ? 'Waiting for permission...' : isBlocked ? 'Try Again' : 'Enable Access'}
          </Button>
          <Button data-testid="cancel-permission-btn" onClick={onCancel}
            variant="ghost" className="w-full text-muted-foreground hover:text-foreground hover:bg-muted/30"
            disabled={isRequesting}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
